// #region Imports
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import Button from "@mui/material/Button";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { useGetTeamsOpenToMyInvitationsQuery, useCreateTeamInvitationMutation } from "marketplace/teams/_api/marketplace-team-api";
import TeamOpenToInvitation from "marketplace/teams/invitations/team-open-to-invitation";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useMemo, useState } from "react";
// #endregion

// #region Styled Components
const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(30),
}));
// #endregion

/** What the picker calls somebody who is already on the team it is offering. */
export const ALREADY_ON_THE_TEAM = "Already on this team";

/** What the viewer is told when the invitation went out. */
export const invitationSentMessage = (teamName: string): string => `Invitation to join ${teamName} sent.`;

/** The fallback when the API refuses without saying why. */
export const INVITATION_REFUSED = "Could not invite this person to the team.";

type ModelStateErrors = Readonly<Record<string, readonly string[]>>;

/**
 * The reason the API gave for refusing an invitation, in the viewer's language.
 *
 * The invitation endpoint reports a refusal it can explain — already a member, already the leader,
 * the invitee cannot reach the marketplace — as a 400 carrying model-state errors. Those sentences
 * are written for the person reading them, so they are shown rather than replaced by a generic
 * failure. Anything else is a fault, not an explanation, and gets the fallback.
 */
export function invitationRefusalMessage(error: unknown): string {
  if (!error || typeof error !== "object" || !("data" in error)) return INVITATION_REFUSED;
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== "object") return INVITATION_REFUSED;
  const errors = data as ModelStateErrors;
  const sentences = Object.values(errors)
    .flatMap((value) => (Array.isArray(value) ? value : []))
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0);
  return sentences.length > 0 ? sentences[0] : INVITATION_REFUSED;
}

/**
 * Sends the invitation and reports the outcome.
 *
 * At module scope on purpose: the React Compiler bails out of any component containing a
 * `try`/`catch` around this shape, and a bail-out would cost the header its memoization. See the
 * React Compiler section of `app/CLAUDE.md`.
 */
async function inviteToTeam(
  createTeamInvitation: (args: { inviteeId: string; teamId: string; message?: string }) => { unwrap: () => Promise<unknown> },
  inviteeId: string,
  team: TeamOpenToInvitation,
): Promise<void> {
  try {
    await createTeamInvitation({ inviteeId, teamId: team.id.value }).unwrap();
    enqueueSnackbar(invitationSentMessage(team.name), { variant: "success" });
  } catch (error) {
    enqueueSnackbar(invitationRefusalMessage(error), { variant: "error" });
  }
}

export type InviteToTeamButtonProps = {
  /** The marketplace individual whose profile is being looked at. */
  inviteeIndividualId: Guid;
};

/**
 * Raises an invitation for the person whose profile you are reading to join one of your teams.
 *
 * The teams it offers, and whether each already has this person on it, come from
 * `marketplace/teams/open-to-my-invitations` — never from a broader team list narrowed here. A
 * viewer who may staff no team gets an empty answer, and this renders nothing at all rather than a
 * disabled control, because an action nobody can take should not take up the header.
 *
 * Choosing a team raises an invitation rather than seating the person directly. That is the only
 * path onto a marketplace team, and it is the one that asks them first.
 */
export default function InviteToTeamButton(props: Readonly<InviteToTeamButtonProps>) {
  const { inviteeIndividualId } = props;

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const teamsQuery = useGetTeamsOpenToMyInvitationsQuery({ inviteeId: inviteeIndividualId.value });
  const [createTeamInvitation] = useCreateTeamInvitationMutation();

  const teamsData = teamsQuery.data;
  const teams = useMemo(
    () => (teamsData ? TeamOpenToInvitation.fromJSONArray([...teamsData]) : []),
    [teamsData],
  );

  if (teams.length === 0) return null;

  return (
    <>
      <StyledButton
        variant="outlined"
        startIcon={<GroupAddIcon />}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        Invite to Team
      </StyledButton>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        slotProps={{ list: { "aria-label": "Teams you can invite this person to" } }}
      >
        {teams.map((team) => (
          <MenuItem
            key={team.id.value}
            disabled={team.inviteeAlreadyBelongs}
            onClick={async () => {
              setAnchor(null);
              await inviteToTeam(createTeamInvitation, inviteeIndividualId.value, team);
            }}
          >
            <ListItemText
              primary={team.name}
              secondary={team.inviteeAlreadyBelongs ? ALREADY_ON_THE_TEAM : undefined}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
